import React, { useEffect, useRef, useState } from 'react';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { getCSSVariableValue } from '../../../assets/ts/_utils';
import { useThemeMode } from '../..';
import { formatCurrency } from '../../../../app/modules/reports/components/ReportHelpers';

interface DonutChartProps {
  series: number[];
  labels: string[];
  className?: string;
  chartHeight?: string;
}

const DonutChart: React.FC<DonutChartProps> = ({
  series,
  labels,
  className,
  chartHeight = '340px'
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();

  // Process data to show only top 5 + others
  const processDataForTop5 = () => {
    // Combine series and labels into objects and sort by value
    const combinedData = series.map((value, index) => ({
      value,
      label: labels[index]
    })).sort((a, b) => b.value - a.value);

    // Take top 5
    const top5 = combinedData.slice(0, 5);
    const others = combinedData.slice(5);
    
    // If there are items beyond top 5, group them as "Others"
    const processedSeries = top5.map(item => item.value);
    const processedLabels = top5.map(item => item.label);
    
    if (others.length > 0) {
      const othersSum = others.reduce((sum, item) => sum + item.value, 0);
      processedSeries.push(othersSum);
      processedLabels.push('Others'.toTranslatedDisplay());
    }
    
    return { processedSeries, processedLabels };
  };

  const { processedSeries, processedLabels } = processDataForTop5();

  const refreshChart = () => {
    if (!chartRef.current) {
      return;
    }

    const chart = new ApexCharts(chartRef.current, chartOptions(processedSeries, processedLabels, chartHeight, series));
    if (chart) {
      chart.render();
    }

    return chart;
  };

  useEffect(() => {
    const chart = refreshChart();

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, mode, processedSeries, processedLabels]);

  return (
    <div className={`card${className ? ` ${className}` : ''}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{'Expense Categories'.toTranslatedDisplay()}</span>
          <span className='text-muted fw-semibold fs-7'>{'Expense by Category'.toTranslatedDisplay()}</span>
        </h3>
      </div>
      <div className='card-body pt-0 pb-5'>
        <div ref={chartRef} />
      </div>
    </div>
  );
};

const chartOptions = (
  series: number[], 
  labels: string[], 
  chartHeight: string,
  originalSeries: number[]
): ApexOptions => {
  const labelColor = getCSSVariableValue('--bs-gray-700');
  const borderColor = getCSSVariableValue('--bs-gray-200');
  const mutedColor = getCSSVariableValue('--bs-gray-500');
  
  // Define color palette - 6 colors for top 5 + others
  const colors = [
    getCSSVariableValue('--bs-primary'),
    getCSSVariableValue('--bs-success'),
    getCSSVariableValue('--bs-warning'),
    getCSSVariableValue('--bs-danger'),
    getCSSVariableValue('--bs-info'),
    getCSSVariableValue('--bs-secondary'), // For "Others" category
  ];

  return {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'donut',
      width: '100%',
      height: chartHeight,
      background: 'transparent',
    },
    labels: labels,
    colors: colors,
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: (value: number) => `${formatCurrency(value)}`,
      },
    },
    stroke: {
      width: 1, // Reduced from 4 to 1 for thinner stroke
      colors: [borderColor],
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      offsetY: 4,
      labels: {
        colors: labelColor,
        useSeriesColors: false,
      },
      markers: {
        size: 8,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
    plotOptions: {
      pie: {
        startAngle: 0,
        endAngle: 360,
        donut: {
          size: '82%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: '600',
              color: labelColor,
              offsetY: -10,
              formatter: (val: string) => val,
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: '700',
              color: labelColor,
              offsetY: 16,
              formatter: (val: string) => formatCurrency(parseFloat(val)),
            },
            total: {
              show: true,
              showAlways: false,
              label: 'Total',
              fontSize: '14px',
              fontWeight: '600',
              color: mutedColor,
              formatter: () => {
                const total = originalSeries.reduce((a, b) => a + b, 0);
                return formatCurrency(total);
              },
            },
          },
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  };
};

export default DonutChart;